import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Copy, Check, Share2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildShareUrl = (token) => `${window.location.origin}/partner/${token}`;

export const PartnerShareCard = ({ mode }) => {
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/partner/invite`, { headers: authHeader() });
        setInvite(res.data?.invite || null);
      } catch {
        /* silent */
      }
    };
    fetchInvite();
  }, []);

  const createInvite = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/partner/invite`, {}, { headers: authHeader() });
      setInvite(res.data);
      toast.success(t("partner.linkReady"));
    } catch {
      toast.error(t("partner.createError"));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!invite?.token) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(invite.token));
      setCopied(true);
      toast.success(t("partner.copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("partner.copyError"));
    }
  };

  const revoke = async () => {
    if (!window.confirm(t("partner.revokeConfirm"))) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/partner/invite`, { headers: authHeader() });
      setInvite(null);
      toast.success(t("partner.revoked"));
    } catch {
      toast.error(t("partner.revokeError"));
    } finally {
      setLoading(false);
    }
  };

  const nativeShare = async () => {
    if (!invite?.token) return;
    const url = buildShareUrl(invite.token);
    const text =
      mode === "pregnancy"
        ? t("partner.shareTitlePregnancy")
        : t("partner.shareTitleTTC");
    if (navigator.share) {
      try {
        await navigator.share({ title: "Sacred Celestial Flow", text, url });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  const ctaTitle = mode === "pregnancy" ? t("partner.inviteTitlePregnancy") : t("partner.inviteTitleTTC");
  const ctaCopy = mode === "pregnancy" ? t("partner.inviteCopyPregnancy") : t("partner.inviteCopyTTC");
  const viewCount = invite?.view_count ?? 0;
  const viewWord = viewCount === 1 ? t("partner.views") : t("partner.views_plural");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="partner-share-card"
    >
      <Card className="bg-gradient-to-br from-rose-500/10 via-pink-500/5 to-purple-500/10 border-pink-400/20 backdrop-blur">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-full bg-pink-500/20 flex items-center justify-center">
              <Heart className="w-5 h-5 text-pink-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white tracking-tight">{ctaTitle}</h3>
              <p className="text-sm text-white/70 mt-1 leading-relaxed">{ctaCopy}</p>

              {!invite ? (
                <Button
                  onClick={createInvite}
                  disabled={loading}
                  data-testid="create-partner-link-btn"
                  className="mt-4 bg-pink-500 hover:bg-pink-600 text-white rounded-full"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {loading ? t("partner.creating") : t("partner.createLink")}
                </Button>
              ) : (
                <div className="mt-4 space-y-3">
                  <div
                    className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/10 px-3 py-2 overflow-hidden"
                    data-testid="partner-link-display"
                  >
                    <span className="text-xs text-white/80 font-mono truncate flex-1">
                      {buildShareUrl(invite.token)}
                    </span>
                    <button
                      type="button"
                      onClick={copyLink}
                      className="shrink-0 text-white/70 hover:text-white transition"
                      data-testid="copy-partner-link-btn"
                      aria-label={t("partner.copied")}
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={nativeShare}
                      size="sm"
                      className="bg-pink-500 hover:bg-pink-600 text-white rounded-full"
                      data-testid="share-partner-link-btn"
                    >
                      <Share2 className="w-4 h-4 mr-1.5" /> {t("partner.share")}
                    </Button>
                    <Button
                      type="button"
                      onClick={revoke}
                      size="sm"
                      variant="outline"
                      disabled={loading}
                      className="border-white/20 text-white/80 hover:bg-white/5 rounded-full"
                      data-testid="revoke-partner-link-btn"
                    >
                      <X className="w-4 h-4 mr-1.5" /> {t("partner.revoke")}
                    </Button>
                  </div>
                  <p className="text-xs text-white/50">
                    {t("partner.expires")} {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "—"}
                    {" • "}
                    {viewCount} {viewWord}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PartnerShareCard;
